import * as XLSX from 'xlsx';
import type { StatutoryMatrix } from './types';
import { headTotals } from './statutoryTotals';

/**
 * Export the PF/ESIC/PT reconciliation as a workbook accounts can work in.
 *
 * WHY THE EVIDENCE COLUMNS TRAVEL WITH THE NUMBERS
 * The point of this screen is that every figure can be traced to a document. An export that
 * carries only the amounts throws that away and turns a checkable reconciliation back into a
 * spreadsheet of assertions. So each row also carries the challan reference, the Zoho voucher
 * number, and the bank statement page — the three things somebody needs to verify a line without
 * opening the dashboard at all.
 *
 * Zoho vouchers are joined with "; " into one cell rather than spread across numbered columns.
 * Most months have one; September 2025 PF has two, and a fixed set of voucher columns would be
 * mostly empty and would silently truncate the month that matters.
 */

/** Excel rejects sheet names over 31 chars or containing : \ / ? * [ ] — and corrupts silently. */
const sheetName = (label: string, taken: Set<string>) => {
	const base = label.replace(/[:\\/?*[\]]/g, ' ').slice(0, 31).trim() || 'Sheet';
	let name = base;
	for (let i = 2; taken.has(name); i++) name = `${base.slice(0, 28)} ${i}`;
	taken.add(name);
	return name;
};

type Cell = string | number | null;

const sheetFrom = (aoa: Cell[][]) => XLSX.utils.aoa_to_sheet(aoa);

export function exportStatutoryMatrix(matrix: StatutoryMatrix) {
	const wb = XLSX.utils.book_new();
	const taken = new Set<string>();

	// ---- Sheet 1: documents held, per head per month -------------------------------------------
	const docHeader = [
		'Head',
		'Wage month',
		'Paid in',
		'Challan reference',
		'As per challan',
		'As per return',
		'Left the bank',
		'Bank date',
		'How matched',
		'Bank statement',
		'Page',
		'Challan document',
		'7Q interest',
		'14B damages',
		'People',
		'Wages',
	];
	const docRows: Cell[][] = [];
	for (const line of matrix.lines) {
		for (const m of line.months) {
			const be = m.bank_evidence?.[0];
			docRows.push([
				line.line,
				m.wage_month,
				m.payment_month,
				m.reference,
				m.challan_rs,
				m.return_rs,
				m.bank_rs,
				be?.bank_date ?? m.bank_date,
				m.bank_match_type,
				be?.statement ?? null,
				// "page 26 of 91" reads as a place to look; a bare 26 reads as a quantity.
				be?.page ? `${be.page}${be.of_pages ? ` of ${be.of_pages}` : ''}` : null,
				m.challan_document?.document_title ?? null,
				m.interest_7q_rs,
				m.damages_14b_rs,
				m.members,
				m.wages_rs,
			]);
		}
	}
	XLSX.utils.book_append_sheet(wb, sheetFrom([docHeader, ...docRows]), sheetName('Documents held', taken));

	// ---- One sheet per Zoho ledger -------------------------------------------------------------
	const headHeader = [
		'Month',
		'Zoho month',
		'As per challan',
		'As per return',
		'As per Zoho',
		'Difference',
		'Status',
		'Zoho vouchers',
	];
	for (const line of matrix.lines) {
		for (const h of line.heads) {
			const rows: Cell[][] = h.rows.map(r => [
				r.wage_month,
				r.zoho_month,
				r.challan_rs,
				r.return_rs,
				r.zoho_rs,
				r.diff_rs,
				r.status,
				(r.zoho_entries ?? [])
					.map(e => `${e.zoho_label ?? e.zoho_transaction_id ?? '?'} (${e.amount_rs})`)
					.join('; ') || null,
			]);
			// Same rule as the screen — see statutoryTotals.ts. If the workbook totalled differently
			// from the dashboard, whichever one somebody quoted first would become the truth.
			const t = headTotals(h);
			const totals: Cell[] = [
				`Total (${t.comparableMonths} months with a document)`,
				null,
				t.documentRs,
				null,
				t.zohoRs,
				t.netRs,
				`${h.totals.tied_months} of 12 tie`,
				null,
			];
			const notes: Cell[][] = [];
			if (t.excludedMonths.length) {
				notes.push([
					`Excluded — no challan or return on file: ${t.excludedMonths.join(', ')}. Zoho booked ${t.excludedZohoRs} in those months.`,
				]);
			}
			if (t.notBookedMonths.length) {
				notes.push([
					`Document exists but nothing booked: ${t.notBookedMonths.join(', ')} — ${t.notBookedRs}. Counted in the total above.`,
				]);
			}
			// The account name, not the head key: it is what accounts will search Zoho for.
			XLSX.utils.book_append_sheet(
				wb,
				sheetFrom([[h.label], [`Zoho account: ${h.zoho_account} (${h.zoho_side})`], [h.question], [], headHeader, ...rows, totals, [], ...notes]),
				sheetName(h.zoho_account + (h.zoho_side === 'credit' ? ' Cr' : ' Dr'), taken)
			);
		}
	}

	// ---- What is missing -----------------------------------------------------------------------
	const gapRows: Cell[][] = matrix.gaps.map(g => [g.line, g.wage_month, g.kind, g.amount_rs, g.detail]);
	XLSX.utils.book_append_sheet(
		wb,
		sheetFrom([['Head', 'Month', 'What is missing', 'Amount', 'Detail'], ...gapRows]),
		sheetName('What is missing', taken)
	);

	XLSX.writeFile(wb, `statutory_pf_esic_pt_${matrix.fy}.xlsx`);
}
